/**
 * Turning what a person typed into the instants a reservation is stored as.
 *
 * A booked window is written on a screen as Polish wall-clock time — "12 to 14
 * August", "Tuesday, 10:00 to 14:00" — and stored as two absolute instants.
 * Those are different things, and the conversion is the whole content of this
 * file. Get it wrong and a studio block booked for 10:00 in January is stored
 * an hour off from the same block booked for 10:00 in July, because Poland
 * moves its clocks and UTC does not.
 *
 * No dependency does this: `Date` has no notion of a zone other than the host's
 * and `Temporal` is not in the Node this runs on. So the offset is measured
 * rather than assumed — see {@link zonedInstant}.
 *
 * Pure, and unit-tested (`period.test.ts`) at the two hours of the year that
 * actually break naive implementations.
 */

import { blockedWindow } from './buffers.ts'

const TZ = 'Europe/Warsaw'

/** en-CA renders `YYYY-MM-DD, HH:mm:ss` — parseable back into parts. */
const partsFmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

/**
 * How far ahead of UTC Poland is AT a given instant, in milliseconds.
 *
 * Read off the formatter: take the instant, ask what the wall clock says in
 * Warsaw, and treat that reading as if it were UTC. The difference between the
 * two is the offset — 1h in winter, 2h in summer, and correct on the day it
 * changes without a table of transition dates.
 */
interface ZonedParts {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
  readonly second: number
}

/**
 * What a Warsaw wall clock reads at a given instant.
 *
 * The one place that asks ICU what time it is over there. Everything below —
 * the offset, the calendar day, the minutes past midnight — is arithmetic on
 * this, so there is exactly one piece of zone reasoning in the package and the
 * DST tests exercise all of it.
 */
function zonedPartsOf(instant: Date): ZonedParts {
  const parts = partsFmt.formatToParts(instant)
  const get = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0')
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    // `hour12: false` renders midnight as 24 in some ICU versions.
    hour: get('hour') % 24,
    minute: get('minute'),
    second: get('second'),
  }
}

function offsetMsAt(instant: Date): number {
  const p = zonedPartsOf(instant)
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asIfUtc - instant.getTime()
}

/**
 * The Warsaw calendar day an instant falls on, as `YYYY-MM-DD`.
 *
 * The inverse of {@link zonedInstant}, and the reason it cannot be
 * `toISOString().slice(0, 10)`: that is the UTC day, which disagrees with the
 * Warsaw one for the first one or two hours of every day. An order placed at
 * 00:30 on the 21st is a UTC order on the 20th, and every cutoff and deadline
 * computed from it would be a day early.
 */
export function zonedDay(instant: Date): string {
  const p = zonedPartsOf(instant)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/** Minutes past Warsaw midnight at an instant — what a cutoff hour compares to. */
export function zonedMinutes(instant: Date): number {
  const p = zonedPartsOf(instant)
  return p.hour * 60 + p.minute
}

/**
 * `YYYY-MM-DD` shifted by whole calendar days.
 *
 * Calendar arithmetic, deliberately NOT instant arithmetic: adding 86 400 000ms
 * to a Warsaw midnight lands at 23:00 or 01:00 on the two clock-change days, so
 * "tomorrow" would come out as today or as the day after. `Date.UTC` normalises
 * an out-of-range day (the 32nd of August) into the next month, and UTC has no
 * transitions to trip over.
 */
export function addDays(iso: string, days: number): string {
  const shifted = new Date(
    Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)) + days),
  )
  return shifted.toISOString().slice(0, 10)
}

/**
 * Polish wall-clock time → the instant it names.
 *
 * Two passes, and the second one is not belt-and-braces. The offset depends on
 * the instant, and the instant is what we are solving for, so the first pass
 * uses the offset at the WRONG moment — off by an hour for anything within an
 * hour of a clock change. Re-measuring at the candidate answer fixes it.
 *
 * The hour that does not exist (02:00–03:00 on the last Sunday in March) lands
 * on 03:00, and the hour that happens twice in October resolves to the first
 * of them. Both are the conventional readings and neither can be right for
 * everybody; what matters is that they are stable.
 */
export function zonedInstant(dayISO: string, minutesOfDay = 0): Date {
  const year = Number(dayISO.slice(0, 4))
  const month = Number(dayISO.slice(5, 7))
  const day = Number(dayISO.slice(8, 10))
  const naive = Date.UTC(year, month - 1, day, 0, minutesOfDay)

  const first = naive - offsetMsAt(new Date(naive))
  return new Date(naive - offsetMsAt(new Date(first)))
}

/** `HH:MM` → minutes past midnight. Returns `null` on anything else. */
export function minutesOfClock(clock: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(clock)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** Whole days from one `YYYY-MM-DD` to another, both ends counted. */
export function inclusiveDays(fromISO: string, toISO: string): number {
  const from = Date.UTC(
    Number(fromISO.slice(0, 4)),
    Number(fromISO.slice(5, 7)) - 1,
    Number(fromISO.slice(8, 10)),
  )
  const to = Date.UTC(
    Number(toISO.slice(0, 4)),
    Number(toISO.slice(5, 7)) - 1,
    Number(toISO.slice(8, 10)),
  )
  return Math.round((to - from) / 86_400_000) + 1
}

/**
 * The billed length of a clocked window, in whole hours.
 *
 * Rounded UP, because a room booked from 10:00 to 11:30 is not free at 11:00
 * and the hour it is not free in is an hour somebody paid for. `billedUnits` is
 * an integer column and the studio prices by the hour; a fractional unit would
 * have to be rounded somewhere, and rounding it DOWN would bill for less time
 * than the room is actually held.
 */
export function billedHours(startsAt: Date, endsAt: Date): number {
  const minutes = (endsAt.getTime() - startsAt.getTime()) / 60_000
  return Math.max(1, Math.ceil(minutes / 60))
}

/** A period, as this domain stores one: half-open, `[startsAt, endsAt)`. */
export interface BookedPeriod {
  readonly startsAt: Date
  readonly endsAt: Date
  readonly isAllDay: boolean
  readonly billedUnits: number
  /**
   * What it takes out of the CALENDAR — the paid window plus its unpaid buffer
   * (spec §10 · I1). Equal to the paid window when the buffer is zero, which
   * is everything but a studio today.
   *
   * Carried on the period rather than added by the caller, so that the one
   * function that knows a window is the one function that knows what it blocks
   * — a caller that had to remember would eventually not.
   */
  readonly blockedFrom: Date
  readonly blockedTo: Date
}

/**
 * A run of whole days, `fromISO` to `toISO` inclusive.
 *
 * The end is midnight at the START of the day after the last one, because the
 * period is half-open: a hire on the 12th alone runs to the 13th at 00:00, and
 * that is exactly what makes the next hire on the 13th not a clash.
 */
export function allDayPeriod(fromISO: string, toISO: string, bufferMinutes = 0): BookedPeriod {
  const startsAt = zonedInstant(fromISO)
  const endsAt = zonedInstant(nextDay(toISO))
  return {
    startsAt,
    endsAt,
    isAllDay: true,
    billedUnits: inclusiveDays(fromISO, toISO),
    ...blockedWindow(startsAt, endsAt, bufferMinutes),
  }
}

/** One day, from one clock time to another. */
export function clockPeriod(
  dayISO: string,
  fromMinutes: number,
  toMinutes: number,
  bufferMinutes = 0,
): BookedPeriod {
  const startsAt = zonedInstant(dayISO, fromMinutes)
  const endsAt = zonedInstant(dayISO, toMinutes)
  return {
    startsAt,
    endsAt,
    isAllDay: false,
    billedUnits: billedHours(startsAt, endsAt),
    ...blockedWindow(startsAt, endsAt, bufferMinutes),
  }
}

/**
 * The period one CHECKOUT line reserves, built from the two strings a basket
 * carries — the same two shapes `CheckoutLineRef` documents: `YYYY-MM-DD` for
 * whole days, `YYYY-MM-DDTHH:MM` for an hour of a room.
 *
 * It exists beside the panel's own resolver rather than reusing it because the
 * two take different input: the panel has a form with four fields, an order
 * line has a window. What they must NOT differ on is the arithmetic, so both
 * end in {@link allDayPeriod} / {@link clockPeriod}.
 *
 * `null` for anything malformed — a reversed window, a clock that is not a
 * clock, or a timed window whose ends fall on different days (studio hours do
 * not cross midnight; a window that claims to is a bug, not a long booking).
 */
export function orderLinePeriod(
  fromISO: string,
  toISO: string,
  bufferMinutes = 0,
): BookedPeriod | null {
  if (!fromISO || !toISO) return null

  if (!fromISO.includes('T')) {
    if (toISO < fromISO) return null
    return allDayPeriod(fromISO, toISO, bufferMinutes)
  }

  const [day, fromClock] = fromISO.split('T')
  const [toDay, toClock] = toISO.split('T')
  if (!day || !fromClock || !toClock || toDay !== day) return null

  const from = minutesOfClock(fromClock.slice(0, 5))
  const to = minutesOfClock(toClock.slice(0, 5))
  if (from === null || to === null || to <= from) return null

  return clockPeriod(day, from, to, bufferMinutes)
}

function nextDay(iso: string): string {
  return addDays(iso, 1)
}
